import { getCurrentInstance } from 'vue'

/**
 * Returns shadow-root-aware DOM query helpers.
 *
 * When the menu runs as a custom element with a shadow root, document.getElementById /
 * document.querySelector / document.activeElement all target the light DOM and miss every
 * element that lives inside the shadow tree.  This composable resolves the correct root
 * (ShadowRoot when present, Document otherwise) so that the same code works in both the
 * CE (shadow DOM) and the platform app (regular Vue) contexts.
 *
 * Must be called during component setup() so getCurrentInstance() resolves correctly.
 */
export function useMenuRoot() {
  const instance = getCurrentInstance()

  function getRoot(): ShadowRoot | Document {
    const el = instance?.proxy?.$el as Element | undefined
    if (!el) return document
    const root = el.getRootNode()
    return root instanceof ShadowRoot ? root : document
  }

  function getElementById(id: string): HTMLElement | null {
    if (!id) return null
    return getRoot().querySelector(`#${id}`) as HTMLElement | null
  }

  function querySelector(selector: string): Element | null {
    return getRoot().querySelector(selector)
  }

  function getActiveElement(): Element | null {
    return getRoot().activeElement
  }

  return { getRoot, getElementById, querySelector, getActiveElement }
}
